import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { TenantCapabilities } from '@/shared/api/tenantSettings';
import classes from '@/shared/components/ConsoleNavbar.module.css';
import { Icon } from '@/shared/components/Icon';

import { ADMIN_GROUPS, canOpen, entryAvailable } from './adminManifest';

/** The two-level navigation of `/admin` (13 §3.3): the console menu gives way to the index by domain. */
export function AdminNavbar({
  permissions,
  capabilities,
  onNavigate,
}: {
  permissions: ReadonlySet<string>;
  capabilities: TenantCapabilities;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('admin');

  return (
    <nav className={classes.navbar} aria-label={t('index.title')}>
      <Link
        to="/"
        className={classes.item}
        style={{ marginTop: 8, color: 'var(--sl-nav-muted)' }}
        onClick={onNavigate}
      >
        <Icon name="arrowLeft" />
        <span className={classes.label}>{t('index.backToConsole')}</span>
      </Link>
      {ADMIN_GROUPS.map((group) => (
        <div key={group.key}>
          <div className={classes.sectionLabel}>{t(`group.${group.key}`)}</div>
          {group.entries
            .filter((entry) => entry.alias !== true && entryAvailable(entry, capabilities))
            .map((entry) =>
              entry.implemented && canOpen(entry, permissions) ? (
                <Link
                  key={entry.labelKey}
                  to={entry.to}
                  className={classes.item}
                  activeProps={{ className: `${classes.item} ${classes.active}` }}
                  onClick={onNavigate}
                >
                  <span className={classes.label}>{t(`entry.${entry.labelKey}`)}</span>
                </Link>
              ) : (
                <span
                  key={entry.labelKey}
                  className={`${classes.item} ${classes.soon}`}
                  aria-disabled="true"
                >
                  <span className={classes.label}>{t(`entry.${entry.labelKey}`)}</span>
                  {entry.implemented ? null : (
                    <span className={classes.soonTag}>{t('state.soon')}</span>
                  )}
                </span>
              ),
            )}
        </div>
      ))}
      <div className={classes.spacer} />
    </nav>
  );
}
